import { UserStatus } from "@/types/user-statuses.types";

/**
 * Centralized configuration for user status styles
 * Contains all color and style definitions for each status
 */
const STATUS_STYLES = {
    [UserStatus.OFFLINE]: {
        fill: "fill-gray-500 dark:fill-gray-400",
        background: "bg-gray-500 dark:bg-gray-400/10 border-gray-500 dark:border-gray-400",
        border: "border-gray-500 dark:border-gray-400",
        text: "text-gray-600 dark:text-gray-400",
        circle: "bg-gray-500 dark:bg-gray-400",
    },
    [UserStatus.INACTIVE]: {
        fill: "fill-orange-500 dark:fill-orange-400",
        background: "bg-orange-400/10 border-orange-500 dark:border-orange-400",
        border: "border-orange-500 dark:border-orange-400",
        text: "text-orange-600 dark:text-orange-400",
        circle: "bg-orange-500 dark:bg-orange-400",
    },
    [UserStatus.IDLE]: {
        fill: "fill-green-500 dark:fill-green-400",
        background: "bg-green-400/10 border-green-500 dark:border-green-400",
        border: "border-green-500 dark:border-green-400",
        text: "text-green-600 dark:text-green-400",
        circle: "bg-green-500 dark:bg-green-400",
    },
    [UserStatus.ACTIVE]: {
        fill: "fill-blue-500 dark:fill-blue-400",
        background: "bg-blue-400/10 border-blue-500 dark:border-blue-400",
        border: "border-blue-500 dark:border-blue-400",
        text: "text-blue-600 dark:text-blue-400",
        circle: "bg-blue-500 dark:bg-blue-400",
    },
    [UserStatus.SILENT]: {
        fill: "fill-yellow-500 dark:fill-yellow-400",
        background: "bg-yellow-400/10 border-yellow-500 dark:border-yellow-400",
        border: "border-yellow-500 dark:border-yellow-400",
        text: "text-yellow-600 dark:text-yellow-400",
        circle: "bg-yellow-500 dark:bg-yellow-400",
    },
    [UserStatus.IN_CALL]: {
        fill: "fill-purple-500 dark:fill-purple-400",
        background: "bg-purple-400/10 border-purple-500 dark:border-purple-400",
        border: "border-purple-500 dark:border-purple-400",
        text: "text-purple-600 dark:text-purple-400",
        circle: "bg-purple-500 dark:bg-purple-400",
    },
} as const;

/**
 * Get fill color classes for user status (for SVG fills, backgrounds, etc.)
 * @param status - User status
 * @param assigned - Whether the seat is assigned
 * @returns Tailwind CSS classes for fill color
 */
export const getStatusFillColor = (status: UserStatus, assigned: boolean = true) => {
    if (!assigned) return "fill-gray-300 dark:fill-gray-600"; // Unassigned seats
    return STATUS_STYLES[status]?.fill || STATUS_STYLES[UserStatus.OFFLINE].fill;
};

/**
 * Get background color classes for user status (for borders, backgrounds, etc.)
 * @param status - User status
 * @returns Tailwind CSS classes for background color
 */
export const getStatusBackgroundColor = (status: UserStatus | null) => {
    const currentStatus = status ?? UserStatus.OFFLINE;
    return STATUS_STYLES[currentStatus]?.background || STATUS_STYLES[UserStatus.OFFLINE].background;
};

/**
 * Get border color classes for user status
 * @param status - User status
 * @returns Tailwind CSS classes for border color
 */
export const getStatusBorderColor = (status: UserStatus | null) => {
    const currentStatus = status ?? UserStatus.OFFLINE;
    return STATUS_STYLES[currentStatus]?.border || STATUS_STYLES[UserStatus.OFFLINE].border;
};

/**
 * Get text color classes for user status
 * @param status - User status
 * @returns Tailwind CSS classes for text color
 */
export const getStatusTextColor = (status: UserStatus) => {
    return STATUS_STYLES[status]?.text || STATUS_STYLES[UserStatus.OFFLINE].text;
};

/**
 * Get status icon for display
 * @param status - User status
 * @returns Icon string
 */
export const getStatusIcon = (status: UserStatus) => {
    if (status === UserStatus.OFFLINE || status === UserStatus.SILENT || status === UserStatus.INACTIVE) return "○";
    return "●";
};

/**
 * Get circle background color classes for user status (theme-aware)
 * @param status - User status
 * @param assigned - Whether the seat is assigned
 * @returns Tailwind CSS classes for circle background color
 */
export const getStatusCircleColor = (status: UserStatus, assigned: boolean = true) => {
    if (!assigned) return "bg-gray-300 dark:bg-gray-600"; // Unassigned seats
    return STATUS_STYLES[status]?.circle || STATUS_STYLES[UserStatus.OFFLINE].circle;
};

/**
 * Get badge variant for user status
 * @param status - User status
 * @returns Badge variant string
 */
export const getStatusVariant = (status: UserStatus) => {
    switch (status) {
        case UserStatus.IDLE:
            return "outline";
        case UserStatus.ACTIVE:
            return "default";
        case UserStatus.IN_CALL:
            return "secondary";
        case UserStatus.SILENT:
        case UserStatus.OFFLINE:
        case UserStatus.INACTIVE:
            return "outline";
        default:
            return "outline";
    }
};

/**
 * Generate status items configuration for display
 * @param t - Translation function
 * @param userStatuses - User statuses record
 * @returns Array of status items with counts
 */
export const getStatusItems = (t: (key: string) => string, userStatuses: Record<string, UserStatus>) => {
    const idleAgents = Object.values(userStatuses).filter((status) => status === UserStatus.IDLE).length;
    const activeAgents = Object.values(userStatuses).filter((status) => status === UserStatus.ACTIVE).length;
    const silentAgents = Object.values(userStatuses).filter((status) => status === UserStatus.SILENT).length;
    const inCallAgents = Object.values(userStatuses).filter((status) => status === UserStatus.IN_CALL).length;
    const inactiveAgents = Object.values(userStatuses).filter((status) => status === UserStatus.INACTIVE).length;

    return [
        { label: t("user_status.idle"), count: idleAgents, status: UserStatus.IDLE },
        { label: t("user_status.active"), count: activeAgents, status: UserStatus.ACTIVE },
        { label: t("user_status.inCall"), count: inCallAgents, status: UserStatus.IN_CALL },
        { label: t("user_status.silent"), count: silentAgents, status: UserStatus.SILENT },
        { label: t("user_status.inactive"), count: inactiveAgents, status: UserStatus.INACTIVE },
    ];
};

/**
 * Get status legend items for display
 * @param t - Translation function
 * @returns Array of legend items with status, label, and optional flags
 */
export const getStatusLegendItems = (t: (key: string) => string) => {
    return [
        { status: UserStatus.OFFLINE, label: t("unassigned"), isUnassigned: true },
        { status: UserStatus.OFFLINE, label: t("user_status.offline") },
        { status: UserStatus.IDLE, label: t("user_status.idle") },
        { status: UserStatus.ACTIVE, label: t("user_status.active") },
        { status: UserStatus.IN_CALL, label: t("user_status.inCall") },
        { status: UserStatus.SILENT, label: t("user_status.silent") },
        { status: UserStatus.INACTIVE, label: t("user_status.inactive") },
    ];
};
